import { Sprite, Texture, Container, Graphics } from 'pixi.js';
import { SymbolData } from '../config/GameConfig';
import { COLORS } from '../config/Colors';

export class Symbol extends Container {
  public sprite: Sprite;
  public symbolData: SymbolData;
  public symbolId: string;
  private glowBackground: Graphics;
  private isHighlighted: boolean = false;
  private glowAnimationId: number | null = null;
  private glowPhase: number = 0;
  
  constructor(symbolData: SymbolData, texture: Texture) {
    super();
    
    this.symbolData = symbolData;
    this.symbolId = symbolData.id;
    
    // Create glowing background (initially invisible)
    this.glowBackground = new Graphics();
    this.glowBackground.visible = false;
    this.addChild(this.glowBackground);
    
    // Create sprite on top of glow
    this.sprite = new Sprite(texture);
    this.sprite.width = 96;
    this.sprite.height = 96;
    this.sprite.anchor.set(0.5);
    this.addChild(this.sprite);
  }
  
  public highlight(): void {
    if (this.isHighlighted) return;
    this.isHighlighted = true;
    
    // Draw glowing background
    this.drawGlow(0.4);
    this.glowBackground.visible = true;
    
    // Animate the glow
    this.animateGlow();
    
    // Scale up the symbol
    this.scale.set(1.15);
  }
  
  private drawGlow(alpha: number): void {
    this.glowBackground.clear();
    
    // Outer glow (larger, more transparent)
    this.glowBackground.roundRect(-55, -55, 110, 110, 12);
    this.glowBackground.fill({ color: COLORS.WIN_GOLD, alpha: alpha * 0.3 });
    
    // Inner glow (smaller, more opaque)
    this.glowBackground.roundRect(-52, -52, 104, 104, 10);
    this.glowBackground.fill({ color: COLORS.WIN_GOLD, alpha: alpha * 0.5 });
    
    // Core highlight
    this.glowBackground.roundRect(-50, -50, 100, 100, 8);
    this.glowBackground.fill({ color: 0xFFFFAA, alpha: alpha * 0.2 });
  }
  
  private animateGlow(): void {
    if (!this.isHighlighted) return;
    
    this.glowPhase += 0.08;
    // Pulse between 0.3 and 0.7
    const alpha = 0.5 + Math.sin(this.glowPhase) * 0.2;
    
    this.drawGlow(alpha);
    
    this.glowAnimationId = requestAnimationFrame(() => this.animateGlow());
  }
  
  public reset(): void {
    this.isHighlighted = false;
    
    // Stop animation
    if (this.glowAnimationId !== null) {
      cancelAnimationFrame(this.glowAnimationId);
      this.glowAnimationId = null;
    }
    
    // Hide glow background
    this.glowBackground.visible = false;
    this.glowBackground.clear();
    
    // Reset sprite
    this.sprite.tint = 0xFFFFFF;
    this.scale.set(1);
    
    this.glowPhase = 0;
  }
  
  public getMultiplier(matchCount: number): number {
    if (matchCount < 3 || matchCount > 5) return 0;
    return this.symbolData.multipliers[matchCount];
  }
}